import type {
  IBlockInteractionsSettings,
  Modification,
  NavigationEditSchemaType,
} from '@carboncopyco/types';
import {
  Button,
  Checkbox,
  InlineLink,
  RadioGroup,
  TextCaption,
  notify,
} from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Card } from '../RulesManager';
import { InputTextField } from '../TextReplaceForm/InputTextField';

export interface BlockInteractionsSettingsProps {
  isEditScreenVisible: boolean;
  setIsEditScreenVisible: (value: boolean) => void;
  createBlockInteractionsSettings: ({
    isActive,
    tooltipPosition,
    tooltipText,
    tooltipTheme,
  }: Omit<IBlockInteractionsSettings, 'type'>) => Promise<
    Modification<NavigationEditSchemaType.blockInteractionsSettings>
  >;
  blockInteractionsSettings: Modification<NavigationEditSchemaType.blockInteractionsSettings>;
}

const initialBlockInteractionsSettings: Pick<
  IBlockInteractionsSettings,
  'tooltipText' | 'tooltipTheme' | 'isActive'
> = {
  tooltipText: 'This part of the app is not available',
  tooltipTheme: 'dark',
  isActive: false,
};

const MAX_LENGTH = 75;
export const BlockInteractionsSettings = ({
  createBlockInteractionsSettings,
  isEditScreenVisible,
  setIsEditScreenVisible,
  blockInteractionsSettings,
}: BlockInteractionsSettingsProps) => {
  const settings =
    blockInteractionsSettings?.content as IBlockInteractionsSettings;
  const { isActive, tooltipText, tooltipTheme } = settings ?? {
    isActive: initialBlockInteractionsSettings.isActive,
    tooltipText: initialBlockInteractionsSettings.tooltipText,
    tooltipTheme: initialBlockInteractionsSettings.tooltipTheme,
  };
  const [isCheckboxChecked, { toggle: toggleCheckbox, set: setIsActive }] =
    useBoolean(isActive);
  const [textValue, setTextValue] = useState(tooltipText);
  const [theme, setTheme] = useState(tooltipTheme); // ["dark", "light"
  const [textError, setTextError] = useState('');

  const isSaveButtonDisabled =
    textValue === tooltipText && theme === tooltipTheme;

  const changeEditScreenVisibility = (visibility = !isEditScreenVisible) => {
    if (visibility) {
      setTextValue(tooltipText);
      setTheme(tooltipTheme);
    }
    setIsEditScreenVisible(visibility);
  };

  useEffect(() => {
    setTheme(tooltipTheme);
  }, [tooltipTheme]);

  useEffect(() => {
    setTextValue(tooltipText);
  }, [tooltipText]);

  useEffect(() => {
    setIsActive(isActive);
  }, [isActive]);

  const cancelBlockInteractionSettings = () => {
    setTextError('');
    changeEditScreenVisibility(false);
  };

  const saveBlockInteractionSettings = async () => {
    setTextError('');

    await onCreateBlockInteractionsSettings({
      isActive: true,
      tooltipPosition: 'bottom',
      tooltipText: textValue,
      tooltipTheme: theme,
    });

    setIsEditScreenVisible(false);
  };

  const onCreateBlockInteractionsSettings = async ({
    isActive,
    tooltipPosition,
    tooltipText,
    tooltipTheme,
  }: Omit<IBlockInteractionsSettings, 'type'>) => {
    try {
      await createBlockInteractionsSettings?.({
        isActive,
        tooltipPosition,
        tooltipText,
        tooltipTheme,
      });
    } catch (_e) {
      notify.error('failed to create rule');
    }
  };
  const oneClickCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    toggleCheckbox();
    if (!e.target.checked) {
      setIsEditScreenVisible(false);
    }
    if (!textError) {
      onCreateBlockInteractionsSettings({
        isActive: e.target.checked,
        tooltipText: tooltipText,
      });
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex justify-between">
        <Checkbox
          label={
            <TextCaption
              label="Use tooltip on blocked elements"
              color="text-ds-natural-600"
            />
          }
          name="activeBlockInteraction"
          checked={isCheckboxChecked}
          onChange={oneClickCheckbox}
        />
        <InlineLink
          underline
          disabled={!isCheckboxChecked}
          as={Button}
          label="Edit"
          color="text-ds-natural-600"
          onClick={() => changeEditScreenVisibility()}
        />
      </div>
      {isEditScreenVisible && (
        <Card>
          <Card.Content>
            <div className="flex flex-col gap-5 p-1.5 pt-2.5">
              <InputTextField
                autoFocus
                multiline
                errorMessage={textError}
                label="Tooltip content"
                name="input block interaction tooltip content"
                value={textValue}
                placeholder="This part of the app is not available"
                onChange={(value) => {
                  if (value.length <= MAX_LENGTH && value) setTextError('');
                  setTextValue(value);
                }}
              />
              <div className="flex-col gap-1.5">
                <TextCaption
                  label="Tooltip’s background color"
                  color="text-ds-natural-600"
                />
                <RadioGroup
                  horizontal
                  radios={[
                    {
                      label: 'Dark',
                      id: '1',
                      value: 'dark',
                    },
                    {
                      label: 'Light',
                      id: '2',
                      value: 'light',
                    },
                  ]}
                  defaultValue={theme}
                  onChange={(value) =>
                    setTheme(
                      value as IBlockInteractionsSettings['tooltipTheme']
                    )
                  }
                  value={theme}
                />
              </div>
              <div className="flex justify-end gap-1">
                <Button
                  label="Cancel"
                  secondary
                  link
                  onClick={cancelBlockInteractionSettings}
                />
                <Button
                  disabled={isSaveButtonDisabled}
                  label="Save"
                  onClick={() => {
                    if (textValue === '') {
                      setTextError('Please enter tooltip content');
                      return;
                    }
                    if (textValue.length > MAX_LENGTH) {
                      setTextError('Can contain up to 75 characters.');
                      return;
                    }
                    saveBlockInteractionSettings();
                  }}
                />
              </div>
            </div>
          </Card.Content>
        </Card>
      )}
      {!isEditScreenVisible && <div className="pt-4" />}
    </div>
  );
};
